import { useQuery } from '@tanstack/react-query';
import { useEffect } from 'react';
import { AxiosError } from 'axios';
import { apiClient } from '@api/client';
import { useAuthStore } from '@store/authStore';
import type { GoogleChatConnection, SlackConnection, TeamsConnection, TenantSummary, User } from '@store/authStore';

interface CurrentUserResponse {
  id: string;
  email: string;
  first_name: string;
  last_name: string;
  status: string;
  is_admin: boolean;
  realm_id?: string;
  onboarding_complete?: boolean;
  active_tenant_id?: string | null;
  active_tenant?: TenantSummary | null;
  available_tenants?: TenantSummary[];
  permissions?: string[];
  slack_connection?: SlackConnection | null;
  teams_connection?: TeamsConnection | null;
  google_chat_connection?: GoogleChatConnection | null;
}

/**
 * Hook to check and restore authentication on app load
 * - On mount: Restores session from localStorage
 * - Validates session with backend by calling /auth/me
 * - If session is invalid (401), clears auth and returns error
 * - If valid, updates store with current user info
 */
export const useCheckAuth = (enabled = true) => {
  const { setCurrentUser, clearAuth } = useAuthStore();

  const result = useQuery({
    queryKey: ['auth', 'me'],
    queryFn: async () => {
      // If no session cookie is present, this will fail with 401.
      const { data } = await apiClient.get<CurrentUserResponse>(
        '/auth/me'
      );

      // Convert response to User model
      const user: User = {
        id: data.id,
        email: data.email,
        first_name: data.first_name,
        last_name: data.last_name,
        status: data.status,
        is_admin: data.is_admin,
        realm_id: data.realm_id,
        onboarding_complete: data.onboarding_complete,
        active_tenant_id: data.active_tenant_id ?? null,
        active_tenant: data.active_tenant ?? null,
        available_tenants: data.available_tenants ?? [],
        permissions: data.permissions ?? [],
        slack_connection: data.slack_connection ?? null,
        teams_connection: data.teams_connection ?? null,
        google_chat_connection: data.google_chat_connection ?? null,
      };

      // Update store with current user info
      setCurrentUser(user);

      return user;
    },
    // Always run on mount to check/restore session
    // (returns error if no valid session)
    enabled,
    // Stale time: 5 minutes - auth info doesn't change often
    staleTime: 5 * 60 * 1000,
    // Don't retry - if auth fails once, it's likely invalid
    retry: 0,
    // Don't refetch on window focus
    refetchOnWindowFocus: false,
  });

  // Handle auth errors in useEffect
  useEffect(() => {
    if (result.error instanceof AxiosError) {
      // If auth fails (401/403), clear auth state
      // The apiClient already does this, but ensure it's cleared here too
      if (result.error.response?.status === 401 || result.error.response?.status === 403) {
        console.log('[useCheckAuth] Auth validation failed, clearing auth state');
        clearAuth();
      }
    }
  }, [result.error, clearAuth]);

  return result;
};
